import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Trash2, KeyRound } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { useCan } from '../../hooks/usePermission';
import { Button, Field, Input, Select, Sheet, Tabs } from '../ui';
import { PermissionMatrix } from './PermissionMatrix';
import { ROLE_SCOPES, extrasOnly, countActions, type PermissionsMap, type Registry } from './permissions';
import type { Role } from './RoleSheet';
import { PhoneInput } from '../ui/PhoneInput';

export type User = {
  id: string; email: string; firstName: string | null; lastName: string | null; fullName: string | null; phone: string | null;
  roleId: string; roleName: string | null; region: 'UK' | 'AE'; entityId: string | null; entityName: string | null; isActive: boolean; hasPassword: boolean;
  permissionOverrides: PermissionsMap; scopeType: string | null; scopeIds: string[]; lastLoginAt: string | null;
};
type Entity = { id: string; name: string; shortName: string | null; region: string };
type Property = { id: string; name: string; city: string | null; region: string };

const SCOPE_LABELS: Record<string, string> = { ALL: 'Everything', REGION: 'Their region', ENTITY: 'Selected companies', PROPERTY: 'Selected properties', OWN: 'Their own records' };
const scopeMeaning = (k: string) => ROLE_SCOPES.find(([s]) => s === k)?.[1]?.toLowerCase() ?? k.toLowerCase();

/** `scope` '' = inherit the role's scope (sent as scopeType null). */
interface Draft { email: string; firstName: string; lastName: string; phone: string; region: 'UK' | 'AE'; entityId: string; roleId: string; isActive: boolean; password: string; scope: string; scopeIds: string[]; overrides: PermissionsMap }

/**
 * Create (user = null) or edit a person: profile, one role, what they can see, and extra rights. Overrides only ever add
 * on top of the role (the server merges them), so taking a right away means changing the role.
 */
export function UserSheet({ user, roles, registry, onClose, onSaved }: { user: User | null; roles: Role[]; registry: Registry; onClose: () => void; onSaved: () => void }) {
  const me = useAuth((s) => s.user);
  const qc = useQueryClient();
  const canUpdate = useCan('users.update'); const canCreate = useCan('users.create'); const canDelete = useCan('users.delete');
  const isNew = !user;
  const defaultRole = roles.find((r) => r.key === 'staff') ?? roles[0];
  const [draft, setDraft] = useState<Draft>(() => ({
    email: user?.email ?? '', firstName: user?.firstName ?? '', lastName: user?.lastName ?? '', phone: user?.phone ?? '',
    region: user?.region ?? 'UK', entityId: user?.entityId ?? '', roleId: user?.roleId ?? defaultRole?.id ?? '', isActive: user?.isActive ?? true, password: '',
    scope: user?.scopeType ?? '', scopeIds: user?.scopeIds ?? [], overrides: JSON.parse(JSON.stringify(user?.permissionOverrides ?? {})),
  }));
  const [tab, setTab] = useState<'profile' | 'permissions'>('profile');
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const needsIds = draft.scope === 'PROPERTY' || draft.scope === 'ENTITY';
  const entities = useQuery({ queryKey: ['entities', 'all'], queryFn: async () => (await api.get<Entity[]>('/entities', { perPage: 100 })).data });
  const properties = useQuery({ queryKey: ['properties', 'picker'], queryFn: async () => (await api.get<Property[]>('/properties', { perPage: 100, sort: 'name' })).data, enabled: draft.scope === 'PROPERTY' });

  const role = roles.find((r) => r.id === draft.roleId);
  const extras = useMemo(() => extrasOnly(registry, draft.overrides, role?.permissions, role?.allAccess), [registry, draft.overrides, role]);
  const extraCount = countActions(extras, registry);

  const payload = () => ({
    email: draft.email.trim(), firstName: draft.firstName.trim() || null, lastName: draft.lastName.trim() || null, phone: draft.phone.trim() || null,
    roleId: draft.roleId, region: draft.region, entityId: draft.entityId || null, isActive: draft.isActive,
    ...(draft.password ? { password: draft.password } : {}),
    permissionOverrides: extras, scopeType: draft.scope || null, scopeIds: needsIds ? draft.scopeIds : draft.scope === 'REGION' ? [draft.region] : [], // REGION's one id is the region code
  });
  const save = useMutation({
    mutationFn: async () => (isNew ? api.post<User>('/users', payload()) : api.patch<User>(`/users/${user!.id}`, payload())),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['roles'] }); onSaved(); },
  });
  const remove = useMutation({ mutationFn: async () => api.delete(`/users/${user!.id}`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['roles'] }); onSaved(); } });

  const valid = draft.email.includes('@') && !!draft.roleId && (!draft.password || draft.password.length >= 10) && (!needsIds || draft.scopeIds.length > 0);
  const readOnly = isNew ? !canCreate : !canUpdate;

  return (
    <Sheet open onClose={onClose} size="lg" eyebrow={isNew ? 'New user' : user?.roleName} title={isNew ? 'Invite someone' : user?.fullName || user?.email}
      subtitle={!isNew && <>{user?.email}{user?.hasPassword ? '' : ' · has not set a password yet'}</>}
      below={
        <Tabs className="shrink-0 px-3 sm:px-4" items={[{ key: 'profile', label: 'Profile' }, { key: 'permissions', label: 'Permissions', count: extraCount ? extraCount : null }]} value={tab} onChange={setTab} />
      }
      footer={
        <div className="w-full">
          {(save.error || remove.error) && <p className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700">{((save.error ?? remove.error) as Error).message}</p>}
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-1">
              {!isNew && !readOnly && user && user.id !== me?.id && <ResetPasswordButton user={user} />}
              {!isNew && canDelete && user?.id !== me?.id && (
                <Button variant="ghost" size="sm" onClick={() => { if (confirm(`Remove ${user?.fullName || user?.email}? Their sessions end immediately and their history is kept.`)) remove.mutate(); }} disabled={remove.isPending}><Trash2 className="h-3.5 w-3.5" /> Remove user</Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              {!readOnly && <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>{save.isPending ? 'Saving…' : isNew ? (draft.password ? 'Create user' : 'Create & invite') : 'Save changes'}</Button>}
            </div>
          </div>
        </div>
      }>
      {tab === 'profile' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name"><Input value={draft.firstName} onChange={(e) => set('firstName', e.target.value)} className="w-full" disabled={readOnly} /></Field>
          <Field label="Last name"><Input value={draft.lastName} onChange={(e) => set('lastName', e.target.value)} className="w-full" disabled={readOnly} /></Field>
          <Field label="Email"><Input type="email" value={draft.email} onChange={(e) => set('email', e.target.value)} className="w-full" disabled={readOnly} placeholder="name@lotusliving.co.uk" /></Field>
          <Field label="Phone"><PhoneInput value={draft.phone} onChange={(v) => set('phone', v)} disabled={readOnly} /></Field>
          <Field label="Region"><Select value={draft.region} onChange={(e) => set('region', e.target.value as 'UK' | 'AE')} className="w-full" disabled={readOnly}><option value="UK">United Kingdom</option><option value="AE">United Arab Emirates</option></Select></Field>
          <Field label="Company" hint="Which legal entity employs or houses them."><Select value={draft.entityId} onChange={(e) => set('entityId', e.target.value)} className="w-full" disabled={readOnly}><option value="">None</option>{entities.data?.filter((en) => en.region === draft.region).map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}</Select></Field>
          <Field label="Role" hint={role ? (role.allAccess ? 'Full access' : role.description ?? undefined) : undefined}>
            <Select value={draft.roleId} onChange={(e) => set('roleId', e.target.value)} className="w-full" disabled={readOnly}>{roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select>
          </Field>
          <Field label="Can see">
            {/* Ids belong to one company/property scope, so any switch starts the pick again. */}
            <Select value={draft.scope} onChange={(e) => setDraft((d) => ({ ...d, scope: e.target.value, scopeIds: [] }))} className="w-full" disabled={readOnly}>
              <option value="">Same as role ({role ? scopeMeaning(role.scopeType) : '—'})</option>
              {ROLE_SCOPES.map(([k]) => <option key={k} value={k}>{SCOPE_LABELS[k] ?? k}</option>)}
            </Select>
          </Field>
          {needsIds && (
            <div className="sm:col-span-2">
              <Picker
                label={draft.scope === 'PROPERTY' ? 'Properties they may see' : 'Companies they may see'}
                items={draft.scope === 'PROPERTY' ? (properties.data ?? []).map((p) => ({ id: p.id, label: p.name, hint: p.city ?? p.region })) : (entities.data ?? []).map((e) => ({ id: e.id, label: e.name, hint: e.region }))}
                selected={draft.scopeIds} onChange={(ids) => set('scopeIds', ids)} disabled={readOnly} />
            </div>
          )}
          <Field label={isNew ? 'Password' : 'New password'} hint={isNew ? 'Leave blank to invite without one; they cannot sign in until it is set.' : 'Leave blank to keep the current password.'}>
            <div className="relative"><KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" /><Input type="password" value={draft.password} onChange={(e) => set('password', e.target.value)} className="w-full pl-9" disabled={readOnly} placeholder="At least 10 characters" autoComplete="new-password" /></div>
          </Field>
          <label className="flex items-start gap-3 rounded-lg border border-ink-200 p-3 text-[13px]">
            <input type="checkbox" checked={draft.isActive} disabled={readOnly || user?.id === me?.id} onChange={(e) => set('isActive', e.target.checked)} className="mt-0.5 h-4 w-4 accent-bronze-500" />
            <span><span className="font-medium text-ink-900">Active</span><span className="block text-[11.5px] text-ink-500">Inactive users keep their history but cannot sign in. The last full-access account cannot be deactivated.</span></span>
          </label>
        </div>
      ) : (
        <div>
          <div className="mb-1 text-[12px] font-medium text-ink-600">Page permissions</div>
          {role?.allAccess ? (
            <div className="rounded-lg border border-ink-200 p-4 text-[12.5px] text-ink-500">This role already grants every action, so nothing can be added on top.</div>
          ) : (
            <>
              <p className="mb-3 text-[11.5px] text-ink-400">Ticked and grey = from the role. Tick more to add rights for this person only. To remove a right, change the role.</p>
              <PermissionMatrix registry={registry} value={draft.overrides} onChange={(overrides) => set('overrides', overrides)} locked={role?.permissions} disabled={readOnly} toolbar={false} />
            </>
          )}
        </div>
      )}
    </Sheet>
  );
}

function Picker({ label, items, selected, onChange, disabled }: { label: string; items: Array<{ id: string; label: string; hint?: string }>; selected: string[]; onChange: (ids: string[]) => void; disabled?: boolean }) {
  const [q, setQ] = useState('');
  const shown = items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-lg border border-ink-200">
      <div className="flex items-center gap-2 border-b border-ink-100 px-3 py-2">
        <span className="text-[12px] font-medium text-ink-700">{label}</span>
        <span className="text-[11px] text-ink-400">{selected.length} selected</span>
        <div className="relative ml-auto"><Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter" className="w-36 rounded-lg border border-ink-200 py-1 pl-8 pr-2 text-[12px] outline-none focus:border-bronze-400" /></div>
      </div>
      <ul className="max-h-48 divide-y divide-ink-100 overflow-y-auto">
        {shown.map((i) => (
          <li key={i.id}><label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-[12.5px] hover:bg-ink-50/60"><input type="checkbox" disabled={disabled} checked={selected.includes(i.id)} onChange={(e) => onChange(e.target.checked ? [...selected, i.id] : selected.filter((x) => x !== i.id))} className="h-4 w-4 accent-bronze-500" /><span className="text-ink-800">{i.label}</span>{i.hint && <span className="text-[11px] text-ink-400">{i.hint}</span>}</label></li>
        ))}
        {!shown.length && <li className="px-3 py-4 text-center text-[12px] text-ink-400">Nothing matches.</li>}
      </ul>
    </div>
  );
}

/**
 * An administrator's password reset (Task R, prototype §15/§14): a temporary password shown once, here, to pass on;
 * the person must choose their own at the next sign-in, and every session they had is ended.
 */
function ResetPasswordButton({ user }: { user: User }) {
  const [temp, setTemp] = useState<string | null>(null);
  const reset = useMutation({
    mutationFn: async () => (await api.post<{ temporaryPassword: string }>(`/users/${user.id}/reset-password`, {})).data,
    onSuccess: (d) => setTemp(d.temporaryPassword),
  });
  if (temp) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
        Temporary password for {user.email}: <code className="select-all rounded bg-white px-1.5 py-0.5 font-mono text-[12.5px] text-ink-900">{temp}</code>
        <span className="block text-[11px]">Shown once. They must choose a new one when they sign in.</span>
      </div>
    );
  }
  return (
    <>
      <Button variant="ghost" size="sm" disabled={reset.isPending} onClick={() => { if (confirm(`Reset the password for ${user.fullName || user.email}? They are signed out everywhere and must choose a new password at their next sign-in.`)) reset.mutate(); }}>
        <KeyRound className="h-3.5 w-3.5" /> {reset.isPending ? 'Resetting…' : 'Reset password'}
      </Button>
      {reset.error && <span className="text-[11.5px] text-red-600">{(reset.error as Error).message}</span>}
    </>
  );
}
